/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Button, Modal, Text } from '@/shared/ui';

type WorkspaceDeleteDialogProps = {
  agencyName: string;
  open: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Confirms abandoning a draft office before it is deleted.
 *
 * Only ever opened for a `no_subscription` agency -- the backend's own
 * `abandon_agency` refuses anything that already holds a subscription -- so
 * the warning only ever has to say that the draft itself is gone for good.
 *
 * @param props.agencyName - The draft's name, named back in the warning.
 * @param props.open - Whether the dialog is shown.
 * @param props.busy - Whether the delete request is in flight.
 * @param props.error - A failure to report, or null.
 * @param props.onCancel - Closes the dialog without deleting anything.
 * @param props.onConfirm - Confirms the delete.
 * @returns The confirmation dialog.
 */
export function WorkspaceDeleteDialog({
  agencyName, open, busy, error, onCancel, onConfirm,
}: WorkspaceDeleteDialogProps) {
  const { t } = useTranslation('@pages/agencies');

  return (
    <Modal
      data-testid="workspace-delete-dialog"
      opened={open}
      onClose={onCancel}
      title={t('workspace.deleteDraft.title')}
      size="sm"
    >
      <div className="flex flex-col gap-(--space-4)" style={{ padding: 'var(--space-2) 0' }}>
        <Text style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-primary)' }}>
          {t('workspace.deleteDraft.message', { name: agencyName })}
        </Text>
        {error && (
          <Text
            data-testid="workspace-delete-error"
            style={{ fontSize: 'var(--text-meta)', color: 'var(--status-error-text)' }}
          >
            {error}
          </Text>
        )}
        <div className="flex flex-wrap justify-end gap-(--space-2)">
          <Button type="button" variant="subtle" onClick={onCancel} disabled={busy}>
            {t('workspace.deleteDraft.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="workspace-delete-confirm"
            onClick={onConfirm}
            disabled={busy}
            className="bg-(--seed-base) hover:bg-(--seed-base)/90 text-(--text-inverse)"
          >
            {t('workspace.deleteDraft.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
